import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import '../KG/Modal.css';
import './ImportSchemaModal.css';

interface ImportSchemaModalProps {
  kgId: string;
  versionId: string;
  currentNodes: { label: string; properties: { name: string }[] }[];
  currentEdges: { type: string; properties: { name: string }[] }[];
  onClose: () => void;
  onImport: (importedDescriptions: ImportedDescriptions) => void;
}

interface KGOption {
  kg_id: string;
  kg_name: string;
  versions: string[];
}

interface ImportedDescriptions {
  node_descriptions: {
    [label: string]: {
      __description?: string;
      [property: string]: string | undefined;
    };
  };
  edge_descriptions: {
    [type: string]: {
      __description?: string;
      [property: string]: string | undefined;
    };
  };
}

interface DescriptionChange {
  type: 'node' | 'edge';
  entityName: string;
  field: string;
  oldValue: string;
  newValue: string;
}

const ImportSchemaModal: React.FC<ImportSchemaModalProps> = ({
  kgId,
  versionId,
  currentNodes,
  currentEdges,
  onClose,
  onImport,
}) => {
  const [step, setStep] = useState<'select' | 'review'>('select');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Selection state
  const [kgList, setKgList] = useState<KGOption[]>([]);
  const [selectedKgId, setSelectedKgId] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');

  // Review state
  const [changes, setChanges] = useState<DescriptionChange[]>([]);
  const [importedDescriptions, setImportedDescriptions] = useState<ImportedDescriptions>({
    node_descriptions: {},
    edge_descriptions: {},
  });

  useEffect(() => {
    loadKGList();
  }, []);

  const loadKGList = async () => {
    try {
      setLoading(true);
      const response: any = await apiService.listKGs(true);

      if (response.data) {
        const kgs: KGOption[] = [];
        const seenKgIds = new Set<string>();

        for (const item of response.data) {
          const kgIdFromResponse = item.kg_id || item.id;
          const kgName = item.kg_name || item.attributes?.kg_name || kgIdFromResponse;

          // Skip if we've already seen this KG ID
          if (seenKgIds.has(kgIdFromResponse)) {
            continue;
          }

          try {
            const versionsResponse: any = await apiService.listVersions(kgIdFromResponse, true);
            let versions = versionsResponse.data?.map((v: any) => v.version_id) || [];

            // If this is the same KG as currently opened, exclude the current version
            // This prevents importing descriptions from the same version we're editing
            // Convert both to strings for comparison to handle type mismatches
            if (String(kgIdFromResponse) === String(kgId)) {
              versions = versions.filter((v: string) => String(v) !== String(versionId));
            }

            // Only add this KG if it has at least one valid version remaining
            if (versions.length > 0) {
              kgs.push({
                kg_id: kgIdFromResponse,
                kg_name: kgName,
                versions,
              });
              seenKgIds.add(kgIdFromResponse);
            }
          } catch (err) {
            console.error(`Failed to load versions for KG ${kgIdFromResponse}:`, err);
          }
        }

        setKgList(kgs);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load KG list'));
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToReview = async () => {
    if (!selectedKgId || !selectedVersionId) {
      setError('Please select a KG and version');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch source KG schema and custom attributes
      // Use allSettled to handle potential timeouts gracefully
      const [sourceSchemaResult, sourceCustomAttrsResult] = await Promise.allSettled([
        apiService.getSchema(selectedKgId, selectedVersionId),
        apiService.getCustomAttributes(selectedKgId, selectedVersionId),
      ]);

      // Check if custom attributes fetch failed
      if (sourceCustomAttrsResult.status === 'rejected') {
        console.error('Error fetching source KG custom attributes:', sourceCustomAttrsResult.reason);
        throw sourceCustomAttrsResult.reason;
      }

      const sourceCustomAttrsResponse: any = sourceCustomAttrsResult.value;
      const sourceCustomAttrs = sourceCustomAttrsResponse.data?.attributes?.custom_attributes || {};
      const sourceNodeDescriptions = sourceCustomAttrs.node_descriptions || {};
      const sourceEdgeDescriptions = sourceCustomAttrs.edge_descriptions || {};

      // Get source schema if available (may have timed out)
      let sourceSchema: any = null;
      if (sourceSchemaResult.status === 'fulfilled') {
        const sourceSchemaResponse: any = sourceSchemaResult.value;
        sourceSchema = sourceSchemaResponse.data?.attributes?.schema || sourceSchemaResponse.data?.schema || sourceSchemaResponse.data || {};
      } else {
        console.warn('Source schema fetch timed out or failed, will proceed without schema validation:', sourceSchemaResult.reason);
      }

      // Check if source KG has any descriptions at all
      const hasSourceDescriptions = Object.keys(sourceNodeDescriptions).length > 0 ||
                                      Object.keys(sourceEdgeDescriptions).length > 0;

      if (!hasSourceDescriptions) {
        setError('No descriptions found in the selected source KG');
        setLoading(false);
        return;
      }

      // Fetch current KG schema and custom attributes
      const [currentSchemaResult, currentCustomAttrsResult] = await Promise.allSettled([
        apiService.getSchema(kgId, versionId),
        apiService.getCustomAttributes(kgId, versionId),
      ]);

      // Check if custom attributes fetch failed
      if (currentCustomAttrsResult.status === 'rejected') {
        console.error('Error fetching current KG custom attributes:', currentCustomAttrsResult.reason);
        throw currentCustomAttrsResult.reason;
      }

      const currentCustomAttrsResponse: any = currentCustomAttrsResult.value;
      const currentCustomAttrs = currentCustomAttrsResponse.data?.attributes?.custom_attributes || {};
      const currentNodeDescriptions = currentCustomAttrs.node_descriptions || {};
      const currentEdgeDescriptions = currentCustomAttrs.edge_descriptions || {};

      // Get current schema if available (may have timed out)
      let currentSchema: any = null;
      if (currentSchemaResult.status === 'fulfilled') {
        const currentSchemaResponse: any = currentSchemaResult.value;
        currentSchema = currentSchemaResponse.data?.attributes?.schema || currentSchemaResponse.data?.schema || currentSchemaResponse.data || {};
      } else {
        console.warn('Current schema fetch timed out or failed, will use props data instead:', currentSchemaResult.reason);
      }

      // Extract nodes and edges from schemas or use props
      let sourceNodes: string[] = [];
      let sourceEdges: string[] = [];

      if (sourceSchema && sourceSchema.nodes) {
        sourceNodes = Object.keys(sourceSchema.nodes);
        sourceEdges = Object.keys(sourceSchema.edges || sourceSchema.relationships || {});
      }

      // Use current schema if available, otherwise use props
      let targetNodes: string[] = [];
      let targetEdges: string[] = [];

      if (currentSchema && currentSchema.nodes) {
        targetNodes = Object.keys(currentSchema.nodes);
        targetEdges = Object.keys(currentSchema.edges || currentSchema.relationships || {});
      } else {
        // Fallback to props
        targetNodes = currentNodes.map(n => n.label);
        targetEdges = currentEdges.map(e => e.type);
      }

      const detectedChanges: DescriptionChange[] = [];
      const newImportedDescriptions: ImportedDescriptions = {
        node_descriptions: {},
        edge_descriptions: {},
      };

      // Track if we found any matching entities
      let hasMatchingEntities = false;

      // If we have schema data, check for matching nodes/edges first
      if (sourceNodes.length > 0 && targetNodes.length > 0) {
        const matchingNodes = sourceNodes.filter(node => targetNodes.includes(node));
        const matchingEdges = sourceEdges.filter(edge => targetEdges.includes(edge));

        if (matchingNodes.length === 0 && matchingEdges.length === 0) {
          setError('No matching nodes or edges found between the two KGs');
          setLoading(false);
          return;
        }
      }

      // Process nodes
      currentNodes.forEach(node => {
        const sourceNodeDesc = sourceNodeDescriptions[node.label];

        // Check if this node exists in source schema (if schema available)
        if (sourceNodes.length > 0 && !sourceNodes.includes(node.label)) {
          return; // Skip if node doesn't exist in source schema
        }

        if (sourceNodeDesc) {
          hasMatchingEntities = true;
          newImportedDescriptions.node_descriptions[node.label] = {};

          // Check entity description
          if (sourceNodeDesc.__description) {
            const currentDesc = currentNodeDescriptions[node.label]?.__description || '';
            if (sourceNodeDesc.__description !== currentDesc) {
              detectedChanges.push({
                type: 'node',
                entityName: node.label,
                field: '__description',
                oldValue: currentDesc,
                newValue: sourceNodeDesc.__description,
              });
              newImportedDescriptions.node_descriptions[node.label].__description = sourceNodeDesc.__description;
            }
          }

          // Check property descriptions
          node.properties.forEach(prop => {
            if (sourceNodeDesc[prop.name]) {
              const currentDesc = currentNodeDescriptions[node.label]?.[prop.name] || '';
              if (sourceNodeDesc[prop.name] !== currentDesc) {
                detectedChanges.push({
                  type: 'node',
                  entityName: node.label,
                  field: prop.name,
                  oldValue: currentDesc,
                  newValue: sourceNodeDesc[prop.name],
                });
                newImportedDescriptions.node_descriptions[node.label][prop.name] = sourceNodeDesc[prop.name];
              }
            }
          });
        }
      });

      // Process edges
      currentEdges.forEach(edge => {
        const sourceEdgeDesc = sourceEdgeDescriptions[edge.type];

        // Check if this edge exists in source schema (if schema available)
        if (sourceEdges.length > 0 && !sourceEdges.includes(edge.type)) {
          return; // Skip if edge doesn't exist in source schema
        }

        if (sourceEdgeDesc) {
          hasMatchingEntities = true;
          newImportedDescriptions.edge_descriptions[edge.type] = {};

          // Check entity description
          if (sourceEdgeDesc.__description) {
            const currentDesc = currentEdgeDescriptions[edge.type]?.__description || '';
            if (sourceEdgeDesc.__description !== currentDesc) {
              detectedChanges.push({
                type: 'edge',
                entityName: edge.type,
                field: '__description',
                oldValue: currentDesc,
                newValue: sourceEdgeDesc.__description,
              });
              newImportedDescriptions.edge_descriptions[edge.type].__description = sourceEdgeDesc.__description;
            }
          }

          // Check property descriptions
          edge.properties.forEach(prop => {
            if (sourceEdgeDesc[prop.name]) {
              const currentDesc = currentEdgeDescriptions[edge.type]?.[prop.name] || '';
              if (sourceEdgeDesc[prop.name] !== currentDesc) {
                detectedChanges.push({
                  type: 'edge',
                  entityName: edge.type,
                  field: prop.name,
                  oldValue: currentDesc,
                  newValue: sourceEdgeDesc[prop.name],
                });
                newImportedDescriptions.edge_descriptions[edge.type][prop.name] = sourceEdgeDesc[prop.name];
              }
            }
          });
        }
      });

      // Determine appropriate error message based on what we found
      if (!hasMatchingEntities) {
        setError('No matching nodes or edges found between the two KGs');
        setLoading(false);
        return;
      }

      if (detectedChanges.length === 0) {
        setError('No changes found. Both KGs have the same descriptions for matching entities');
        setLoading(false);
        return;
      }

      setChanges(detectedChanges);
      setImportedDescriptions(newImportedDescriptions);
      setStep('review');
      setLoading(false);
    } catch (err: any) {
      console.error('Import schema error:', err);
      const errorMessage = extractErrorMessage(err, 'Failed to load data from selected KG');
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleConfirmImport = () => {
    onImport(importedDescriptions);
    onClose();
  };

  const selectedKg = kgList.find(kg => kg.kg_id === selectedKgId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content import-schema-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Schema Descriptions</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {step === 'select' && (
          <div className="modal-body">
            <p className="modal-description">
              Select a source KG and version to import schema descriptions from.
              Only descriptions for matching nodes, edges, and properties will be imported.
              {kgList.some(kg => kg.kg_id === kgId) && (
                <span className="current-kg-note">
                  {' '}You can import from other versions of the current KG.
                </span>
              )}
            </p>

            <div className="form-group">
              <label htmlFor="kg-select">Knowledge Graph</label>
              <select
                id="kg-select"
                className="form-control"
                value={selectedKgId}
                onChange={(e) => {
                  setSelectedKgId(e.target.value);
                  setSelectedVersionId('');
                  setError('');
                }}
                disabled={loading}
              >
                <option value="">Select a KG...</option>
                {kgList.map(kg => (
                  <option key={kg.kg_id} value={kg.kg_id}>
                    {kg.kg_name} (ID: {kg.kg_id})
                  </option>
                ))}
              </select>
            </div>

            {selectedKgId && (
              <div className="form-group">
                <label htmlFor="version-select">Version</label>
                <select
                  id="version-select"
                  className="form-control"
                  value={selectedVersionId}
                  onChange={(e) => {
                    setSelectedVersionId(e.target.value);
                    setError('');
                  }}
                  disabled={loading}
                >
                  <option value="">Select a version...</option>
                  {selectedKg?.versions.map(version => (
                    <option key={version} value={version}>
                      Version {version}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="modal-footer">
              <button className="tab-btn tab-btn--ghost" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="tab-btn tab-btn--primary"
                onClick={handleProceedToReview}
                disabled={!selectedKgId || !selectedVersionId || loading}
              >
                {loading ? <span className="inline-spinner" /> : 'Preview Changes'}
              </button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="modal-body">
            <p className="modal-description">
              Review the changes below. {changes.length} description{changes.length !== 1 ? 's' : ''} will be updated.
            </p>

            <div className="changes-list">
              {changes.map((change, index) => (
                <div key={index} className="change-item">
                  <div className="change-header">
                    <span className={`entity-badge ${change.type}`}>
                      {change.type.toUpperCase()}
                    </span>
                    <span className="entity-name">{change.entityName}</span>
                    {change.field !== '__description' && (
                      <>
                        <span className="field-separator">›</span>
                        <span className="property-name">{change.field}</span>
                      </>
                    )}
                  </div>
                  <div className="change-content">
                    <div className="change-old">
                      <span className="change-label">Current:</span>
                      <span className="change-value">
                        {change.oldValue || <em className="empty-value">No description</em>}
                      </span>
                    </div>
                    <div className="change-arrow">→</div>
                    <div className="change-new">
                      <span className="change-label">New:</span>
                      <span className="change-value">{change.newValue}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button className="tab-btn tab-btn--ghost" onClick={() => setStep('select')}>
                Back
              </button>
              <button className="tab-btn tab-btn--primary" onClick={handleConfirmImport}>
                Import {changes.length} Description{changes.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportSchemaModal;
